import React, { useState } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useParams } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';

import { RootState } from '../../../app/rootReducer';
import { Resource } from '../../../common/constants/resource';
import { HttpService } from '../../../common/http.service';
import {
  IEvidencesSummary,
  summaryListActions,
} from '../../tasks/slice/aiSummaryListSlice';
import {
  PromptTypeToTitleMapping,
  TaskTypes,
} from '../../tasks/types/taskTypes';
import Accurate from './Accurate';
import MarkdownRenderer from './MarkdownRenderer';
import Reason from './Reason';
import TimeSaved from './TimeSaved';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export default function EvidencesSummary({
  evidences,
}: {
  evidences: IEvidencesSummary;
}) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const termPolicyId = useSelector(state => state.application.termPolicyId);

  async function onEvidenceClick(refresh: boolean) {
    if (!isLoading && (!evidences.summary || refresh)) {
      setLoading(true);
      const raw = {
        applicationId: id,
        policyId: termPolicyId,
        promptType: evidences.promptType,
        refresh,
        evidenceRequests: evidences.summaries
          .filter(evidence => {
            // Exclude EHR and LabPiQture from JSON file processing - they only need PDF summaries
            return (
              evidence.evidenceType !== TaskTypes.EHR &&
              evidence.evidenceType !== TaskTypes.LAB_PIQTURE
            );
          })
          .map(evidence => ({
            applicationId: id,
            evidenceId: evidence.id,
            evidenceType: evidence.evidenceType,
            fileKey: `${evidence.carrier}/${evidence.product}/${evidence.evidenceType}/${evidence.evidenceTracker}.evidence.json`,
            promptType: evidence.promptType,
            refresh,
          })),
      };
      const url = `automatedEvidences/summary/evidences/info`;
      try {
        const response = await HttpService.getInstance().post(url, raw);
        dispatch(
          summaryListActions.updateSummary({
            ...evidences,
            ...response.parsedBody,
          }),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={() => {
        setExpanded(!expanded);
        onEvidenceClick(false);
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id={evidences.promptType}
      >
        <Typography>
          {PromptTypeToTitleMapping.get(evidences.promptType)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <RefreshButtonContainer>
              <IconButton
                onClick={() => onEvidenceClick(true)}
                title={Resource.REPULL_AI_SUMMARY}
              >
                <RefreshIcon />
              </IconButton>
            </RefreshButtonContainer>
            <MarkdownRenderer text={evidences.summary} />
            <FeedbackContainer elevation={3} variant="outlined">
              <Accurate {...evidences} />
              {evidences.like === true && <TimeSaved {...evidences} />}
              {evidences.like === false && <Reason {...evidences} />}
            </FeedbackContainer>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

const RefreshButtonContainer = styled(Box)({
  justifyContent: 'flex-end',
  display: 'flex',
});

const FeedbackContainer = styled(Paper)(() => ({
  backgroundColor: '#E6E6E6',
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  padding: '15px',
  marginTop: '15px',
}));
